// ===== Packages =====

import {
    Descendant,
    Element,
    Text,
} from 'slate';

// ===== Interfaces =====

import {
    IAudioTextSyncMatch,
    IAudioTextSyncResult,
    ITranscriptionWord,
} from '../interfaces';

interface ITextWordToken {
    normalizedWord: string,
    path: number[],
    pathKey: string,
    startOffset: number,
    endOffset: number,
}

interface ITranscriptWordToken {
    normalizedWord: string,
    originalIndex: number,
}

interface IGreedyMatchAnchor {
    transcriptStartIndex: number,
    textStartIndex: number,
}

interface IShingleMatchAnchor {
    transcriptStartIndex: number,
    textStartIndex: number,
    shingleSize: number,
}

interface IBuildAudioTextSyncMapArgs {
    slateValue: Descendant[],
    transcriptWords: ITranscriptionWord[],
    textStartPath?: number[],
}

const textWordTokenCache = new WeakMap<Descendant[], ITextWordToken[]>();
const transcriptWordTokenCache = new WeakMap<ITranscriptionWord[], ITranscriptWordToken[]>();

const WORD_PATTERN = /[A-Za-z0-9]+(?:['’\u2010\u2011-][A-Za-z0-9]+)*/g;
const MATCH_LOOKAHEAD_DISTANCE = 12;
const MATCH_INSERTION_RECOVERY_LOOKAHEAD_DISTANCE = 48;
const MATCH_COVERAGE_THRESHOLD = 0.85;
const MATCH_EDGE_SLACK = 3;
const MATCH_LOCAL_NEIGHBORHOOD_DENSITY_THRESHOLD = 0.7;
const MATCH_ANCHOR_WINDOW_SIZE = 48;
const MATCH_ANCHOR_MIN_MATCH_COUNT = 12;
const MATCH_ANCHOR_MIN_SHORT_MATCH_COUNT = 4;
const MATCH_ANCHOR_SEARCH_DISTANCE = 512;
const MATCH_ANCHOR_DENSITY_THRESHOLD = 0.65;
const MATCH_SHINGLE_SIZE = 4;
const MATCH_SHINGLE_MIN_ANCHOR_COUNT = 2;
const READ_ALONG_EXCLUDED_NODE_TYPES = new Set([
    'caption',
    'figure-content',
]);

function shouldSkipReadAlongTextNode(node: Descendant): boolean {
    return Element.isElement(node)
        && typeof node.type === 'string'
        && READ_ALONG_EXCLUDED_NODE_TYPES.has(node.type);
}

/**
 * Normalizes a spoken or written word so transcript and Slate text can be
 * compared without punctuation or casing noise.
 */
function normalizeWord(value: string): string {
    let normalizedWord = value
        .trim()
        .toLowerCase()
        .replace(/[’]/g, '\'')
        .replace(/[\u2010\u2011]/g, '-');

    while (normalizedWord && !/[a-z0-9]/.test(normalizedWord[0])) {
        normalizedWord = normalizedWord.slice(1);
    }
    while (normalizedWord && !/[a-z0-9]/.test(normalizedWord[normalizedWord.length - 1])) {
        normalizedWord = normalizedWord.slice(0, -1);
    }
    while (normalizedWord.startsWith('\'') || normalizedWord.startsWith('-')) {
        normalizedWord = normalizedWord.slice(1);
    }
    while (normalizedWord.endsWith('\'') || normalizedWord.endsWith('-')) {
        normalizedWord = normalizedWord.slice(0, -1);
    }

    return normalizedWord;
}

/**
 * Walks visible Slate descendants and records every word with the exact text
 * offsets needed to decorate that word later.
 */
function collectTextWordTokens(
    nodes: Descendant[],
    parentPath: number[] = [],
    tokens: ITextWordToken[] = [],
): ITextWordToken[] {
    nodes.forEach((node, index) => {
        const path = [...parentPath, index];

        if (shouldSkipReadAlongTextNode(node)) {
            return;
        }

        if (Text.isText(node)) {
            const matches = Array.from(node.text.matchAll(WORD_PATTERN));
            matches.forEach((match) => {
                const matchedWord = match[0] || '';
                const normalizedWord = normalizeWord(matchedWord);
                const startOffset = match.index ?? 0;
                const endOffset = startOffset + matchedWord.length;

                if (!normalizedWord) {
                    return;
                }

                tokens.push({
                    normalizedWord,
                    path,
                    pathKey: path.join(','),
                    startOffset,
                    endOffset,
                });
            });
            return;
        }

        if ('children' in node && Array.isArray(node.children)) {
            collectTextWordTokens(node.children as Descendant[], path, tokens);
        }
    });

    return tokens;
}

/**
 * Keeps transcript word indexes aligned with the source transcript array so the
 * active word can be looked up directly from playback state later.
 */
function collectTranscriptWordTokens(words: ITranscriptionWord[]): ITranscriptWordToken[] {
    const tokens: ITranscriptWordToken[] = [];

    words.forEach((word, index) => {
        const normalizedWord = normalizeWord(word.word || '');
        if (!normalizedWord) {
            return;
        }

        tokens.push({
            normalizedWord,
            originalIndex: index,
        });
    });

    return tokens;
}

/**
 * Tokenizing the full Slate tree is the most expensive setup step in
 * read-along mode. Cache those tokens by array identity so repeat sync-map
 * builds during the same render lifecycle can reuse the work.
 */
function getCachedTextWordTokens(nodes: Descendant[]): ITextWordToken[] {
    const cachedTokens = textWordTokenCache.get(nodes);
    if (cachedTokens) {
        return cachedTokens;
    }

    const nextTokens = collectTextWordTokens(nodes);
    textWordTokenCache.set(nodes, nextTokens);
    return nextTokens;
}

/**
 * Transcript word arrays are also stable by identity for a loaded note, so we
 * can avoid rebuilding normalized transcript tokens every time playback state
 * changes or a sync map is warmed in the background.
 */
function getCachedTranscriptWordTokens(words: ITranscriptionWord[]): ITranscriptWordToken[] {
    const cachedTokens = transcriptWordTokenCache.get(words);
    if (cachedTokens) {
        return cachedTokens;
    }

    const nextTokens = collectTranscriptWordTokens(words);
    transcriptWordTokenCache.set(words, nextTokens);
    return nextTokens;
}

/**
 * Slate decoration paths must remain tied to the original read-only tree, so
 * post-dictation matching cannot simply slice the chapter value after the
 * audio note. Filter the token stream in document order instead.
 */
function comparePathOrder(
    leftPath: number[],
    rightPath: number[],
): number {
    const sharedLength = Math.min(leftPath.length, rightPath.length);

    for (let index = 0; index < sharedLength; index += 1) {
        if (leftPath[index] !== rightPath[index]) {
            return leftPath[index] < rightPath[index]
                ? -1
                : 1;
        }
    }

    if (leftPath.length === rightPath.length) {
        return 0;
    }

    return leftPath.length < rightPath.length
        ? -1
        : 1;
}

function pathStartsWithPrefix(
    path: number[],
    prefix: number[],
): boolean {
    if (path.length < prefix.length) {
        return false;
    }

    for (let index = 0; index < prefix.length; index += 1) {
        if (path[index] !== prefix[index]) {
            return false;
        }
    }

    return true;
}

function getScopedTextWordTokens(data: {
    nodes: Descendant[],
    textStartPath?: number[],
}): ITextWordToken[] {
    const textTokens = getCachedTextWordTokens(data.nodes);
    const normalizedTextStartPath = Array.isArray(data.textStartPath)
        ? data.textStartPath
        : [];

    if (normalizedTextStartPath.length === 0) {
        return textTokens;
    }

    return textTokens.filter((textToken) => (
        !pathStartsWithPrefix(textToken.path, normalizedTextStartPath)
        && comparePathOrder(textToken.path, normalizedTextStartPath) > 0
    ));
}

function buildMatch(
    transcriptToken: ITranscriptWordToken,
    textToken: ITextWordToken,
    textWordIndex: number,
): IAudioTextSyncMatch {
    return {
        transcriptWordIndex: transcriptToken.originalIndex,
        textWordIndex,
        normalizedWord: transcriptToken.normalizedWord,
        path: textToken.path,
        pathKey: textToken.pathKey,
        startOffset: textToken.startOffset,
        endOffset: textToken.endOffset,
    };
}

function buildDirectMatches(
    transcriptTokens: ITranscriptWordToken[],
    textTokens: ITextWordToken[],
): IAudioTextSyncMatch[] {
    const comparableWordCount = Math.min(transcriptTokens.length, textTokens.length);
    const matches: IAudioTextSyncMatch[] = [];

    for (let index = 0; index < comparableWordCount; index += 1) {
        if (transcriptTokens[index].normalizedWord === textTokens[index].normalizedWord) {
            matches.push(buildMatch(
                transcriptTokens[index],
                textTokens[index],
                index,
            ));
        }
    }

    return matches;
}

function buildGreedyMatches(
    transcriptTokens: ITranscriptWordToken[],
    textTokens: ITextWordToken[],
): IAudioTextSyncMatch[] {
    const anchor = findFirstDenseMatchAnchor(transcriptTokens, textTokens);
    return buildGreedyMatchesFromAnchor(transcriptTokens, textTokens, anchor);
}

function findMatchingTextWordIndexInRange(data: {
    transcriptToken: ITranscriptWordToken,
    textTokens: ITextWordToken[],
    textStartIndex: number,
    textEndExclusive: number,
}): number | null {
    const {
        transcriptToken,
        textTokens,
        textStartIndex,
        textEndExclusive,
    } = data;

    for (let textIndex = textStartIndex; textIndex < textEndExclusive; textIndex += 1) {
        if (textTokens[textIndex].normalizedWord === transcriptToken.normalizedWord) {
            return textIndex;
        }
    }

    return null;
}

/**
 * Read-along should stay locked when the visible passage picks up a short
 * inserted clause or sentence after the transcript was recorded. Prefer a
 * tight local search first, then recover across a slightly longer insertion
 * only when no nearby match exists.
 */
function findNextMatchingTextWordIndex(data: {
    transcriptToken: ITranscriptWordToken,
    textTokens: ITextWordToken[],
    textStartIndex: number,
    textEndExclusive?: number,
}): number | null {
    const {
        transcriptToken,
        textTokens,
        textStartIndex,
    } = data;
    const textEndExclusive = Math.max(
        textStartIndex,
        Math.min(textTokens.length, data.textEndExclusive ?? textTokens.length),
    );
    const preferredSearchEndExclusive = Math.min(
        textEndExclusive,
        textStartIndex + MATCH_LOOKAHEAD_DISTANCE,
    );
    const preferredMatch = findMatchingTextWordIndexInRange({
        transcriptToken,
        textTokens,
        textStartIndex,
        textEndExclusive: preferredSearchEndExclusive,
    });

    if (preferredMatch != null || preferredSearchEndExclusive >= textEndExclusive) {
        return preferredMatch;
    }

    const recoverySearchEndExclusive = Math.min(
        textEndExclusive,
        textStartIndex + MATCH_INSERTION_RECOVERY_LOOKAHEAD_DISTANCE,
    );

    if (recoverySearchEndExclusive <= preferredSearchEndExclusive) {
        return null;
    }

    return findMatchingTextWordIndexInRange({
        transcriptToken,
        textTokens,
        textStartIndex: preferredSearchEndExclusive,
        textEndExclusive: recoverySearchEndExclusive,
    });
}

function buildGreedyMatchesInRange(data: {
    transcriptTokens: ITranscriptWordToken[],
    textTokens: ITextWordToken[],
    transcriptStartIndex: number,
    transcriptEndExclusive: number,
    textStartIndex: number,
    textEndExclusive: number,
}): IAudioTextSyncMatch[] {
    const {
        transcriptTokens,
        textTokens,
    } = data;
    const transcriptEndExclusive = Math.max(
        data.transcriptStartIndex,
        Math.min(transcriptTokens.length, data.transcriptEndExclusive),
    );
    const textEndExclusive = Math.max(
        data.textStartIndex,
        Math.min(textTokens.length, data.textEndExclusive),
    );
    const matches: IAudioTextSyncMatch[] = [];
    let textCursor = data.textStartIndex;

    for (
        let transcriptIndex = data.transcriptStartIndex;
        transcriptIndex < transcriptEndExclusive;
        transcriptIndex += 1
    ) {
        const transcriptToken = transcriptTokens[transcriptIndex];
        const matchedTextIndex = findNextMatchingTextWordIndex({
            transcriptToken,
            textTokens,
            textStartIndex: textCursor,
            textEndExclusive,
        });

        if (matchedTextIndex != null) {
            matches.push(buildMatch(
                transcriptToken,
                textTokens[matchedTextIndex],
                matchedTextIndex,
            ));
            textCursor = matchedTextIndex + 1;
        }
    }

    return matches;
}

/**
 * Measures whether the beginning of the transcript forms a dense local match
 * window at a given point in the visible text. This lets us skip post titles
 * or boilerplate before we start the main greedy pass.
 */
function countWindowGreedyMatches(data: {
    transcriptTokens: ITranscriptWordToken[],
    textTokens: ITextWordToken[],
    transcriptStartIndex: number,
    textStartIndex: number,
}): {
    matchedWordCount: number,
    transcriptWindowWordCount: number,
    textWindowWordCount: number,
} {
    const {
        transcriptTokens,
        textTokens,
        transcriptStartIndex,
        textStartIndex,
    } = data;
    const transcriptWindowEndExclusive = Math.min(
        transcriptTokens.length,
        transcriptStartIndex + MATCH_ANCHOR_WINDOW_SIZE,
    );

    if (
        transcriptStartIndex >= transcriptWindowEndExclusive
        || textStartIndex >= textTokens.length
    ) {
        return {
            matchedWordCount: 0,
            transcriptWindowWordCount: 0,
            textWindowWordCount: 0,
        };
    }

    let matchedWordCount = 0;
    let textCursor = textStartIndex;

    for (
        let transcriptIndex = transcriptStartIndex;
        transcriptIndex < transcriptWindowEndExclusive;
        transcriptIndex += 1
    ) {
        const matchedTextIndex = findNextMatchingTextWordIndex({
            transcriptToken: transcriptTokens[transcriptIndex],
            textTokens,
            textStartIndex: textCursor,
        });

        if (matchedTextIndex != null) {
            matchedWordCount += 1;
            textCursor = matchedTextIndex + 1;
        }
    }

    return {
        matchedWordCount,
        transcriptWindowWordCount: transcriptWindowEndExclusive - transcriptStartIndex,
        textWindowWordCount: Math.max(1, textCursor - textStartIndex),
    };
}

/**
 * Short dictated sections cannot satisfy the standard 12-word anchor. Scale
 * the minimum to the available transcript window while keeping a small floor
 * so a couple of repeated words cannot start a false read-along match.
 */
function getMinimumAnchorMatchCount(transcriptWindowWordCount: number): number {
    if (transcriptWindowWordCount <= 0) {
        return MATCH_ANCHOR_MIN_MATCH_COUNT;
    }

    return Math.min(
        MATCH_ANCHOR_MIN_MATCH_COUNT,
        Math.max(
            MATCH_ANCHOR_MIN_SHORT_MATCH_COUNT,
            Math.ceil(transcriptWindowWordCount * MATCH_ANCHOR_DENSITY_THRESHOLD),
        ),
    );
}

/**
 * Finds the first locally dense match window so the main greedy alignment can
 * begin at the dictated passage instead of getting stuck on leading titles or
 * other non-dictated boilerplate above it.
 */
function findFirstDenseMatchAnchor(
    transcriptTokens: ITranscriptWordToken[],
    textTokens: ITextWordToken[],
): IGreedyMatchAnchor {
    if (transcriptTokens.length === 0 || textTokens.length === 0) {
        return {
            transcriptStartIndex: 0,
            textStartIndex: 0,
        };
    }

    const transcriptSearchEndExclusive = Math.min(
        transcriptTokens.length,
        MATCH_EDGE_SLACK + 1,
    );
    const textSearchEndExclusive = Math.min(
        textTokens.length,
        MATCH_ANCHOR_SEARCH_DISTANCE,
    );

    for (let textStartIndex = 0; textStartIndex < textSearchEndExclusive; textStartIndex += 1) {
        for (
            let transcriptStartIndex = 0;
            transcriptStartIndex < transcriptSearchEndExclusive;
            transcriptStartIndex += 1
        ) {
            const windowMatchStats = countWindowGreedyMatches({
                transcriptTokens,
                textTokens,
                transcriptStartIndex,
                textStartIndex,
            });
            const hasMinimumMatches = (
                windowMatchStats.matchedWordCount >= getMinimumAnchorMatchCount(
                    windowMatchStats.transcriptWindowWordCount,
                )
            );

            if (hasMinimumMatches) {
                const transcriptWindowDensity = (
                    windowMatchStats.matchedWordCount / windowMatchStats.transcriptWindowWordCount
                );
                const textWindowDensity = (
                    windowMatchStats.matchedWordCount / windowMatchStats.textWindowWordCount
                );
                const hasDenseLocalMatch = (
                    transcriptWindowDensity >= MATCH_ANCHOR_DENSITY_THRESHOLD
                    && textWindowDensity >= MATCH_ANCHOR_DENSITY_THRESHOLD
                );

                if (hasDenseLocalMatch) {
                    return {
                        transcriptStartIndex,
                        textStartIndex,
                    };
                }
            }
        }
    }

    return {
        transcriptStartIndex: 0,
        textStartIndex: 0,
    };
}

/**
 * Once we know where the first dense local window begins, keep the original
 * greedy alignment logic but start from that anchored passage.
 */
function buildGreedyMatchesFromAnchor(
    transcriptTokens: ITranscriptWordToken[],
    textTokens: ITextWordToken[],
    anchor: IGreedyMatchAnchor,
): IAudioTextSyncMatch[] {
    return buildGreedyMatchesInRange({
        transcriptTokens,
        textTokens,
        transcriptStartIndex: anchor.transcriptStartIndex,
        transcriptEndExclusive: transcriptTokens.length,
        textStartIndex: anchor.textStartIndex,
        textEndExclusive: textTokens.length,
    });
}

function buildShingleKey(data: {
    tokens: Array<ITranscriptWordToken | ITextWordToken>,
    startIndex: number,
    shingleSize: number,
}): string | null {
    const {
        tokens,
        startIndex,
        shingleSize,
    } = data;

    if (startIndex + shingleSize > tokens.length) {
        return null;
    }

    const normalizedWords: string[] = [];
    for (let offset = 0; offset < shingleSize; offset += 1) {
        const normalizedWord = tokens[startIndex + offset]?.normalizedWord || '';
        if (!normalizedWord) {
            return null;
        }

        normalizedWords.push(normalizedWord);
    }

    return normalizedWords.join('\u0001');
}

function buildShingleOccurrenceMap(data: {
    tokens: Array<ITranscriptWordToken | ITextWordToken>,
    shingleSize: number,
}): Map<string, number[]> {
    const occurrences = new Map<string, number[]>();
    const maxStartIndex = data.tokens.length - data.shingleSize;

    for (let startIndex = 0; startIndex <= maxStartIndex; startIndex += 1) {
        const key = buildShingleKey({
            tokens: data.tokens,
            startIndex,
            shingleSize: data.shingleSize,
        });
        if (key) {
            const existingIndexes = occurrences.get(key);
            if (existingIndexes) {
                existingIndexes.push(startIndex);
            } else {
                occurrences.set(key, [startIndex]);
            }
        }
    }

    return occurrences;
}

function buildUniqueShingleAnchorCandidates(data: {
    transcriptTokens: ITranscriptWordToken[],
    textTokens: ITextWordToken[],
    shingleSize: number,
}): IShingleMatchAnchor[] {
    const {
        transcriptTokens,
        textTokens,
        shingleSize,
    } = data;
    const transcriptOccurrences = buildShingleOccurrenceMap({
        tokens: transcriptTokens,
        shingleSize,
    });
    const textOccurrences = buildShingleOccurrenceMap({
        tokens: textTokens,
        shingleSize,
    });
    const candidates: IShingleMatchAnchor[] = [];

    transcriptOccurrences.forEach((transcriptIndexes, key) => {
        const textIndexes = textOccurrences.get(key);
        if (
            transcriptIndexes.length !== 1
            || !textIndexes
            || textIndexes.length !== 1
        ) {
            return;
        }

        candidates.push({
            transcriptStartIndex: transcriptIndexes[0],
            textStartIndex: textIndexes[0],
            shingleSize,
        });
    });

    return candidates.sort((leftAnchor, rightAnchor) => {
        if (leftAnchor.transcriptStartIndex !== rightAnchor.transcriptStartIndex) {
            return leftAnchor.transcriptStartIndex - rightAnchor.transcriptStartIndex;
        }

        return leftAnchor.textStartIndex - rightAnchor.textStartIndex;
    });
}

function findLowerBoundIndex(values: number[], value: number): number {
    let left = 0;
    let right = values.length;

    while (left < right) {
        const middle = Math.floor((left + right) / 2);
        if (values[middle] < value) {
            left = middle + 1;
        } else {
            right = middle;
        }
    }

    return left;
}

function selectMonotonicShingleAnchors(
    candidates: IShingleMatchAnchor[],
): IShingleMatchAnchor[] {
    if (candidates.length === 0) {
        return [];
    }

    const tailTextIndexes: number[] = [];
    const tailCandidateIndexes: number[] = [];
    const previousCandidateIndexes = Array.from(
        { length: candidates.length },
        () => -1,
    );

    candidates.forEach((candidate, candidateIndex) => {
        const tailIndex = findLowerBoundIndex(
            tailTextIndexes,
            candidate.textStartIndex,
        );
        tailTextIndexes[tailIndex] = candidate.textStartIndex;
        tailCandidateIndexes[tailIndex] = candidateIndex;
        previousCandidateIndexes[candidateIndex] = tailIndex > 0
            ? tailCandidateIndexes[tailIndex - 1]
            : -1;
    });

    const anchors: IShingleMatchAnchor[] = [];
    let cursor = tailCandidateIndexes[tailCandidateIndexes.length - 1];
    while (cursor != null && cursor >= 0) {
        anchors.push(candidates[cursor]);
        cursor = previousCandidateIndexes[cursor];
    }

    anchors.reverse();
    const nonOverlappingAnchors: IShingleMatchAnchor[] = [];
    let nextTranscriptStartIndex = 0;
    let nextTextStartIndex = 0;

    anchors.forEach((anchor) => {
        if (
            anchor.transcriptStartIndex < nextTranscriptStartIndex
            || anchor.textStartIndex < nextTextStartIndex
        ) {
            return;
        }

        nonOverlappingAnchors.push(anchor);
        nextTranscriptStartIndex = anchor.transcriptStartIndex + anchor.shingleSize;
        nextTextStartIndex = anchor.textStartIndex + anchor.shingleSize;
    });

    return nonOverlappingAnchors;
}

function buildShingleAnchoredMatches(
    transcriptTokens: ITranscriptWordToken[],
    textTokens: ITextWordToken[],
): IAudioTextSyncMatch[] {
    if (
        transcriptTokens.length < MATCH_SHINGLE_SIZE
        || textTokens.length < MATCH_SHINGLE_SIZE
    ) {
        return [];
    }

    const anchors = selectMonotonicShingleAnchors(
        buildUniqueShingleAnchorCandidates({
            transcriptTokens,
            textTokens,
            shingleSize: MATCH_SHINGLE_SIZE,
        }),
    );
    if (anchors.length < MATCH_SHINGLE_MIN_ANCHOR_COUNT) {
        return [];
    }

    const matches: IAudioTextSyncMatch[] = [];
    const matchedTranscriptWordIndexes = new Set<number>();
    let transcriptCursor = 0;
    let textCursor = 0;
    const pushMatch = (match: IAudioTextSyncMatch): void => {
        if (matchedTranscriptWordIndexes.has(match.transcriptWordIndex)) {
            return;
        }

        matchedTranscriptWordIndexes.add(match.transcriptWordIndex);
        matches.push(match);
    };

    anchors.forEach((anchor) => {
        buildGreedyMatchesInRange({
            transcriptTokens,
            textTokens,
            transcriptStartIndex: transcriptCursor,
            transcriptEndExclusive: anchor.transcriptStartIndex,
            textStartIndex: textCursor,
            textEndExclusive: anchor.textStartIndex,
        }).forEach(pushMatch);

        for (let offset = 0; offset < anchor.shingleSize; offset += 1) {
            pushMatch(buildMatch(
                transcriptTokens[anchor.transcriptStartIndex + offset],
                textTokens[anchor.textStartIndex + offset],
                anchor.textStartIndex + offset,
            ));
        }

        transcriptCursor = anchor.transcriptStartIndex + anchor.shingleSize;
        textCursor = anchor.textStartIndex + anchor.shingleSize;
    });

    buildGreedyMatchesInRange({
        transcriptTokens,
        textTokens,
        transcriptStartIndex: transcriptCursor,
        transcriptEndExclusive: transcriptTokens.length,
        textStartIndex: textCursor,
        textEndExclusive: textTokens.length,
    }).forEach(pushMatch);

    return matches;
}

function matchesSpanTheWholeText(
    matches: IAudioTextSyncMatch[],
    transcriptTokens: ITranscriptWordToken[],
    textTokens: ITextWordToken[],
): boolean {
    if (matches.length === 0) {
        return false;
    }

    const lastMatch = matches[matches.length - 1];
    const lastTranscriptToken = transcriptTokens[transcriptTokens.length - 1];
    const lastTextWordIndex = textTokens.length - 1;
    const transcriptTrailingGap = lastTranscriptToken.originalIndex - lastMatch.transcriptWordIndex;
    const textTrailingGap = lastTextWordIndex - lastMatch.textWordIndex;

    return matches[0].transcriptWordIndex <= MATCH_EDGE_SLACK
        && transcriptTrailingGap <= MATCH_EDGE_SLACK
        && textTrailingGap <= MATCH_EDGE_SLACK;
}

/**
 * Preview surfaces can intentionally render only the first slice of a longer
 * post, and some posts prepend boilerplate above the dictated passage. We
 * still want read-along enabled when the visible text is a strong prefix of a
 * longer transcript, even though the transcript continues past the end of the
 * rendered excerpt and the matching passage may start later in the Slate text.
 */
function matchesSpanTheVisibleTextPrefix(
    matches: IAudioTextSyncMatch[],
    textTokens: ITextWordToken[],
): boolean {
    if (matches.length === 0) {
        return false;
    }

    const firstMatch = matches[0];
    const lastMatch = matches[matches.length - 1];
    const lastTextWordIndex = textTokens.length - 1;
    const textTrailingGap = lastTextWordIndex - lastMatch.textWordIndex;

    return firstMatch.transcriptWordIndex <= MATCH_EDGE_SLACK
        && textTrailingGap <= MATCH_EDGE_SLACK;
}

/**
 * Prevents a few repeated words from enabling read-along when the matches are
 * scattered across a much longer transcript. We still allow some local drift
 * for dropped or paraphrased spoken phrases, but the aligned region must stay
 * reasonably dense in both transcript and visible text.
 */
function matchesStayWithinLocalNeighborhood(
    matches: IAudioTextSyncMatch[],
): boolean {
    if (matches.length === 0) {
        return false;
    }

    const firstMatch = matches[0];
    const lastMatch = matches[matches.length - 1];
    const transcriptNeighborhoodLength = lastMatch.transcriptWordIndex - firstMatch.transcriptWordIndex + 1;
    const textNeighborhoodLength = lastMatch.textWordIndex - firstMatch.textWordIndex + 1;
    const transcriptNeighborhoodDensity = matches.length / transcriptNeighborhoodLength;
    const textNeighborhoodDensity = matches.length / textNeighborhoodLength;

    return transcriptNeighborhoodDensity >= MATCH_LOCAL_NEIGHBORHOOD_DENSITY_THRESHOLD
        && textNeighborhoodDensity >= MATCH_LOCAL_NEIGHBORHOOD_DENSITY_THRESHOLD;
}

/**
 * Leading post boilerplate should not count against the coverage threshold
 * once we have identified the start of the locally dense matched passage.
 */
function getAlignedVisibleTextCoverage(
    matches: IAudioTextSyncMatch[],
    textTokens: ITextWordToken[],
): number {
    if (matches.length === 0 || textTokens.length === 0) {
        return 0;
    }

    const firstMatchedTextWordIndex = matches[0].textWordIndex;
    const alignedVisibleTextWordCount = textTokens.length - firstMatchedTextWordIndex;

    if (alignedVisibleTextWordCount <= 0) {
        return 0;
    }

    return matches.length / alignedVisibleTextWordCount;
}

/**
 * Determines whether a transcript appears to be the word-for-word dictation of
 * the visible Slate content and, if so, returns the text offsets for each
 * transcript word.
 */
const buildAudioTextSyncMap = ({
    slateValue,
    transcriptWords,
    textStartPath = undefined,
}: IBuildAudioTextSyncMapArgs): IAudioTextSyncResult => {
    const textTokens = getScopedTextWordTokens({
        nodes: slateValue,
        textStartPath,
    });
    const transcriptTokens = getCachedTranscriptWordTokens(transcriptWords);
    const emptyResult: IAudioTextSyncResult = {
        enabled: false,
        similarityScore: 0,
        transcriptWordCount: transcriptTokens.length,
        textWordCount: textTokens.length,
        matchedWordCount: 0,
        matches: [],
        matchesByTranscriptWordIndex: Array.from({ length: transcriptWords.length }, () => null),
    };

    if (textTokens.length === 0 || transcriptTokens.length === 0) {
        return emptyResult;
    }

    const directMatches = buildDirectMatches(transcriptTokens, textTokens);
    const greedyMatches = buildGreedyMatches(transcriptTokens, textTokens);
    const shingleAnchoredMatches = buildShingleAnchoredMatches(
        transcriptTokens,
        textTokens,
    );
    const matches = [
        directMatches,
        greedyMatches,
        shingleAnchoredMatches,
    ].reduce((bestMatches, candidateMatches) => (
        candidateMatches.length > bestMatches.length
            ? candidateMatches
            : bestMatches
    ), [] as IAudioTextSyncMatch[]);
    const transcriptCoverage = matches.length / transcriptTokens.length;
    const alignedVisibleTextCoverage = getAlignedVisibleTextCoverage(matches, textTokens);
    const similarityScore = matches.length / Math.max(transcriptTokens.length, textTokens.length);
    const exactPassageMatch = transcriptCoverage >= MATCH_COVERAGE_THRESHOLD
        && alignedVisibleTextCoverage >= MATCH_COVERAGE_THRESHOLD
        && matchesStayWithinLocalNeighborhood(matches)
        && matchesSpanTheWholeText(matches, transcriptTokens, textTokens);
    const visibleTextPrefixMatch = alignedVisibleTextCoverage >= MATCH_COVERAGE_THRESHOLD
        && matchesStayWithinLocalNeighborhood(matches)
        && matchesSpanTheVisibleTextPrefix(matches, textTokens);
    const enabled = exactPassageMatch || visibleTextPrefixMatch;
    const matchesByTranscriptWordIndex = Array.from({ length: transcriptWords.length }, () => null as IAudioTextSyncMatch | null);

    matches.forEach((match) => {
        matchesByTranscriptWordIndex[match.transcriptWordIndex] = match;
    });

    return {
        enabled,
        similarityScore,
        transcriptWordCount: transcriptTokens.length,
        textWordCount: textTokens.length,
        matchedWordCount: matches.length,
        matches,
        matchesByTranscriptWordIndex,
    };
};

export default buildAudioTextSyncMap;
